import React, { useState, useEffect } from 'react';
import { Activity, CheckCircle, XCircle, Clock, TrendingUp, Database, Zap } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function Dashboard() {
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    success: 0,
    failed: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const statsRes = await axios.get(`${API_URL}/pool/stats-bulk`);
      if (statsRes.data) {
        setStats(statsRes.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Dashboard error:', error);
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color = 'cyan', subtext }) => (
    <div className={`bg-gradient-to-br from-${color}-600/10 to-${color}-800/10 border border-${color}-500/30 rounded-xl p-6 hover:border-${color}-500/50 transition-all`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 bg-${color}-600/20 rounded-lg`}>
          <Icon className={`w-6 h-6 text-${color}-400`} />
        </div>
        <span className="text-xs text-gray-400 font-bold">{label}</span>
      </div>
      <div className="text-4xl font-bold text-white mb-1">
        {value.toLocaleString('en-US')}
      </div>
      {subtext && (
        <p className="text-sm text-gray-400">{subtext}</p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-gray-700 rounded-full"></div>
          <div className="w-16 h-16 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  const successRate = stats.success + stats.failed > 0
    ? ((stats.success / (stats.success + stats.failed)) * 100).toFixed(1)
    : 0;

  const completedCount = stats.success + stats.failed;
  const progressPercent = stats.total > 0 
    ? ((completedCount / stats.total) * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Activity className="w-8 h-8 text-cyan-400" />
            Dashboard
          </h1>
          <p className="text-gray-400 mt-1">Real-time test pool statistics</p>
        </div>
        
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 border border-cyan-500/30">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
          <span className="text-sm text-green-400 font-bold">LIVE</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          icon={Database}
          label="TOTAL"
          value={stats.total}
          color="cyan"
        />
        <StatCard
          icon={Clock}
          label="PENDING"
          value={stats.pending}
          color="yellow"
          subtext="In queue"
        />
        <StatCard
          icon={Activity}
          label="PROCESSING"
          value={stats.processing}
          color="blue"
          subtext="Active now"
        />
        <StatCard
          icon={CheckCircle}
          label="SUCCESS"
          value={stats.success}
          color="green"
          subtext={`${successRate}% success rate`}
        />
        <StatCard
          icon={XCircle}
          label="FAILED"
          value={stats.failed}
          color="red"
        />
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Progress Chart */}
        <div className="bg-gray-900 border border-cyan-500/30 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-400" />
            Test Progress
          </h3>

          <div className="space-y-4">
            {/* Overall Progress */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Overall Completion</span>
                <span className="text-sm font-mono text-white font-bold">
                  {completedCount.toLocaleString('en-US')} / {stats.total.toLocaleString('en-US')} ({progressPercent}%)
                </span>
              </div>
              <div className="h-3 bg-black rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-cyan-600 to-blue-600 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Success */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Success</span>
                <span className="text-sm font-mono text-white">
                  {stats.success.toLocaleString('en-US')} ({successRate}%)
                </span>
              </div>
              <div className="h-3 bg-black rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-600 to-emerald-600 transition-all duration-500"
                  style={{ width: stats.total > 0 ? `${(stats.success / stats.total) * 100}%` : '0%' }}
                />
              </div>
            </div>

            {/* Failed */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Failed</span>
                <span className="text-sm font-mono text-white">
                  {stats.failed.toLocaleString('en-US')} ({stats.total > 0 ? ((stats.failed / stats.total) * 100).toFixed(1) : 0}%)
                </span>
              </div>
              <div className="h-3 bg-black rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-red-600 to-orange-600 transition-all duration-500"
                  style={{ width: stats.total > 0 ? `${(stats.failed / stats.total) * 100}%` : '0%' }}
                />
              </div>
            </div>

            {/* Pending */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Pending</span>
                <span className="text-sm font-mono text-white">
                  {stats.pending.toLocaleString('en-US')} ({stats.total > 0 ? ((stats.pending / stats.total) * 100).toFixed(1) : 0}%)
                </span>
              </div>
              <div className="h-3 bg-black rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-yellow-600 to-amber-600 transition-all duration-500"
                  style={{ width: stats.total > 0 ? `${(stats.pending / stats.total) * 100}%` : '0%' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="bg-gray-900 border border-cyan-500/30 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-cyan-400" />
            System Status
          </h3>

          <div className="grid grid-cols-1 gap-4">
            <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-green-500/30">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-sm text-gray-400">Celery Workers</p>
                  <p className="text-lg font-bold text-green-400">Running</p>
                </div>
              </div>
              <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse"></div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-green-500/30">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-sm text-gray-400">Redis Queue</p>
                  <p className="text-lg font-bold text-green-400">Connected</p>
                </div>
              </div>
              <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse"></div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-cyan-500/30">
              <div className="flex items-center gap-3">
                <Activity className="w-8 h-8 text-cyan-400" />
                <div>
                  <p className="text-sm text-gray-400">API Backend</p>
                  <p className="text-lg font-bold text-cyan-400">Online</p>
                </div>
              </div>
              <div className="w-3 h-3 rounded-full bg-cyan-400 animate-pulse"></div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-blue-500/30">
              <div className="flex items-center gap-3">
                <Database className="w-8 h-8 text-blue-400" />
                <div>
                  <p className="text-sm text-gray-400">MySQL Database</p>
                  <p className="text-lg font-bold text-blue-400">Healthy</p>
                </div>
              </div>
              <div className="w-3 h-3 rounded-full bg-blue-400 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 text-center">
          <p className="text-gray-400 text-sm mb-2">Success Rate</p>
          <p className="text-3xl font-bold text-green-400">{successRate}%</p>
        </div>
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 text-center">
          <p className="text-gray-400 text-sm mb-2">Completion</p>
          <p className="text-3xl font-bold text-cyan-400">{progressPercent}%</p>
        </div>
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 text-center">
          <p className="text-gray-400 text-sm mb-2">Active Tests</p>
          <p className="text-3xl font-bold text-blue-400">{stats.processing}</p>
        </div>
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 text-center">
          <p className="text-gray-400 text-sm mb-2">In Queue</p>
          <p className="text-3xl font-bold text-yellow-400">{stats.pending.toLocaleString('en-US')}</p>
        </div>
      </div>
    </div>
  );
}
